import { ReactElement, useCallback, useMemo } from 'react'
import { ColorRepresentation, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import {
  DraggableLine,
  HighlightableLine,
  sceneConfig,
  ImmutableVector3,
} from '@modugen/ui-toolkit'
import { Line } from '@react-three/drei'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import Text3DThree from 'src/components/scene/Text3DThree'
import { useEnhanceClickEvent } from '../../hooks'

export enum BeamElements {
  Line = 'BeamLine',
  Handle = 'BeamHandle',
}
interface Props {
  beam: ShapeObjectLine
  isActive?: boolean
  onClick?: (beamGuid: string) => void
  isSecondary?: boolean
  labelScale?: number
}

const Beam2D = ({
  beam,
  onClick,
  isActive = false,
  isSecondary = false,
  labelScale = 0.1,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()
  const getLabel = useElementLabel()
  const labelPosition = beam.shape.start
    .add(beam.shape.end)
    .divideScalar(2)
    .add(new ImmutableVector3(0, 0, 0.02))
  const showLabels = useControlStore(state => state.showLabels)
  const beamTypeLabelsVisible = useModelStore(
    state => state.labelVisibilityModel.visibilityByType.beams,
  )
  const showBeamLabels = showLabels && beamTypeLabelsVisible
  const points = useMemo(
    () => [beam.shape.start, beam.shape.end].map(({ x, y, z }) => new ImmutableVector3(x, y, z)),
    [beam],
  )

  // Calculate text orientation the same way as ModelMesh
  const textOrientation = useMemo(() => {
    const start = beam.shape.start
    const end = beam.shape.end
    const textXDirection = end.sub(start).normalize().v
    const textYDirection = new Vector3(0, 0, 1).cross(textXDirection).normalize()
    return {
      textXOrientation: textXDirection,
      textYOrientation: textYDirection,
    }
  }, [beam])

  // state picking due to performance reasons
  const mappedColor = useSceneStore(
    useCallback(state => state.guidToColor[beam.guid], [beam.guid]),
  ) as ColorRepresentation | undefined

  const { onClickEnhancedRef, onClickBaseRef, isClickable } = useEnhanceClickEvent(onClick)

  const backgroundLine = useMemo(() => {
    return (
      <group position={[0, 0, -0.001]} layers={sceneConfig.R3FNonSelectableObjectLayer}>
        <Line
          points={points.map(p => p.v)}
          color={'#FFFFFF'}
          lineWidth={scenePalette.elements2d.beams.width}
          segments
          opacity={0.5}
        />
      </group>
    )
  }, [points, scenePalette.elements2d.beams.width])

  const beamColor = (mappedColor || scenePalette.elements2d.beams.color) as string

  const label = showBeamLabels ? (
    <Text3DThree
      textLines={[getLabel(beam.guid)]}
      color={beamColor}
      backgroundColor="#FFFFFF"
      borderColor={beamColor}
      borderWidth={0.005}
      position={labelPosition.v}
      scale={labelScale}
      scaleAxis="Y"
      anchorLocation="center"
      textXOrientation={textOrientation.textXOrientation}
      textYOrientation={textOrientation.textYOrientation}
      opacity={1.0}
    />
  ) : null

  if (isSecondary) {
    return (
      <>
        <HighlightableLine
          key={beam.guid}
          line={points}
          lineName={beam.guid}
          color={scenePalette.elements2d.beams.color as string}
          isHighlighted={false}
          hoverable={false}
          clickable={false}
          opacity={0.65}
          width={scenePalette.elements2d.beams.width}
          onClick={() => onClickBaseRef.current(beam.guid)}
          lineThreeProps={{
            segments: true,
            dashed: scenePalette.elements2d.beams.dashed,
            dashSize: scenePalette.elements2d.beams.dashSize,
            gap: scenePalette.elements2d.beams.gap,
            dashScale: scenePalette.elements2d.beams.dashScale,
            userData: {
              contextMenuActions: {
                hide: {
                  guid: beam.guid,
                },
                properties: {
                  guid: beam.guid,
                },
              },
            },
          }}
        />
        {backgroundLine}
        {label}
      </>
    )
  }

  return (
    <>
      <DraggableLine
        line={{ start: points[0], end: points[1] }}
        color={(mappedColor || scenePalette.elements2d.beams.color) as string}
        selectedColor={scenePalette.elements2d.beams.color as string}
        isSelected={isActive}
        showLengthIndicator={false}
        onClick={() => onClickEnhancedRef.current(beam.guid)}
        showIndicators={false}
        lineName={BeamElements.Line}
        hoverable={isClickable}
        lineThreeProps={{
          lineWidth: isActive ? 4 : scenePalette.elements2d.beams.width,
          segments: true,
          dashed: scenePalette.elements2d.beams.dashed,
          dashSize: scenePalette.elements2d.beams.dashSize,
          gap: scenePalette.elements2d.beams.gap,
          dashScale: scenePalette.elements2d.beams.dashScale,
        }}
      />
      {backgroundLine}
      {label}
    </>
  )
}

export default Beam2D
